import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, X, GitBranch, KeyRound } from "lucide-react";
import {
    referralSummary, referralAssociates, referralHierarchy, changeReferrer, formatApiError,
} from "@/lib/api";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const ROLE_LABEL = { explorer: "Explorer", insider: "Travel Insider", ambassador: "Ambassador", privilege_member: "Privilege Member" };

function flattenTree(root) {
    const out = [];
    const visit = (node, depth) => {
        out.push({ node, depth });
        (node.children || []).forEach((c) => visit(c, depth + 1));
    };
    visit(root, 0);
    return out;
}

function HierarchyTree({ root }) {
    const flat = flattenTree(root);
    return (
        <div>
            {flat.map(({ node, depth }) => (
                <div key={node.id} style={{ marginLeft: depth * 18 }} className="mt-1.5">
                    <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-cream/40 px-3 py-2 text-sm" data-testid={`tree-node-${node.id}`}>
                        <GitBranch className="h-3.5 w-3.5 text-stone-400 shrink-0" />
                        <span className="font-bold text-navy">{node.name}</span>
                        <span className="font-mono text-[10px] text-stone-400">{node.associate_id || "—"}</span>
                        <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-black uppercase text-navy">{ROLE_LABEL[node.role] || node.role}</span>
                        {node.referred_by_name && <span className="ml-auto text-[10px] text-stone-400">via {node.referred_by_name}</span>}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function AcquisitionPanel({ token }) {
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState(null);
    const [category, setCategory] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [tree, setTree] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = {};
            if (category) params.category = category;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const [s, r] = await Promise.all([referralSummary(token, params), referralAssociates(token, params)]);
            setSummary(s);
            setRows(r.associates);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    }, [token, category, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const openTree = async (id) => {
        try { setTree(await referralHierarchy(token, id)); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const doChangeReferrer = async (assoc) => {
        const code = window.prompt(`Change referrer for ${assoc.name}\nEnter a referral code (leave blank to mark as Direct Company Acquisition):`, "");
        if (code === null) return;
        try {
            await changeReferrer(token, assoc.id, code.trim());
            toast.success("Referrer updated");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    return (
        <div className="mt-4 space-y-5" data-testid="acquisition-panel">
            {/* filters */}
            <div className="flex flex-wrap gap-2 rounded-xl border border-stone-200 bg-cream/40 p-3">
                <select className={`${inp} w-auto`} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="acq-filter-category">
                    <option value="">All categories</option>
                    {Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input type="date" className={`${inp} w-auto`} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="acq-filter-from" />
                <input type="date" className={`${inp} w-auto`} value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="acq-filter-to" />
                <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-navy hover:text-saffron" data-testid="acq-refresh">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Apply
                </button>
            </div>

            {/* A. Category-wise acquisition + source summary */}
            {summary ? (
                <div>
                    <h4 className="font-display font-black text-sm text-navy uppercase tracking-wider">Acquisition Summary</h4>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[["Total registered", summary.total_associates], ["Pending", summary.pending], ["Approved", summary.approved], ["Via referral", summary.via_referral],
                          ["Direct company", summary.direct_company], ["Direct referrals", summary.direct_referrals], ["Network referrals", summary.network_referrals], ["Customer bookings", summary.bookings]].map(([l, v]) => (
                            <div key={l} className="rounded-lg border border-stone-200 bg-white p-3">
                                <p className="text-[10px] font-bold uppercase text-stone-500">{l}</p>
                                <p className="font-display font-black text-xl text-navy">{v}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2 rounded-lg border border-stone-200 bg-white p-3 flex flex-wrap items-center gap-4">
                        <span className="text-[10px] font-bold uppercase text-stone-500">Revenue generated</span>
                        <span className="font-display font-black text-xl text-navy">₹{Number(summary.revenue || 0).toLocaleString("en-IN")}</span>
                        <span className="ml-auto text-[10px] font-bold uppercase text-stone-500">By category</span>
                        {Object.entries(summary.by_role || {}).map(([r, n]) => (
                            <span key={r} className="rounded-full bg-navy/10 px-2.5 py-0.5 text-[10px] font-black uppercase text-navy">{ROLE_LABEL[r] || r} · {n}</span>
                        ))}
                    </div>
                </div>
            ) : <div className="py-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>}

            {/* B. Associate-wise referral report */}
            <div>
                <h4 className="font-display font-black text-sm text-navy uppercase tracking-wider">Associate-wise Referral Report</h4>
                <div className="mt-2 overflow-x-auto">
                    {!rows ? <div className="py-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                        <table className="w-full text-sm">
                            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                                <th className="py-2 pr-3">Associate</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Source</th>
                                <th className="py-2 pr-3">Referred by</th><th className="py-2 pr-3">Direct</th><th className="py-2 pr-3">Network</th>
                                <th className="py-2 pr-3">Bookings</th><th className="py-2 pr-3">Revenue</th><th /></tr></thead>
                            <tbody>
                                {rows.map((a) => (
                                    <tr key={a.id} className="border-b border-stone-100" data-testid={`acq-row-${a.id}`}>
                                        <td className="py-2.5 pr-3"><p className="font-bold text-navy leading-tight">{a.name}</p><p className="font-mono text-[10px] text-stone-400">{a.associate_id || "—"}</p></td>
                                        <td className="py-2.5 pr-3 text-xs">{ROLE_LABEL[a.role] || a.role}</td>
                                        <td className="py-2.5 pr-3 text-xs">
                                            {(a.acquisition_source || "direct_company") === "referral"
                                                ? <span className="rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-[10px] font-bold uppercase">Referral</span>
                                                : <span className="rounded-full bg-stone-100 text-stone-500 px-2 py-0.5 text-[10px] font-bold uppercase">Direct</span>}
                                        </td>
                                        <td className="py-2.5 pr-3 text-xs text-stone-500">{a.referred_by_name ? `${a.referred_by_name} (${ROLE_LABEL[a.referred_by_category] || a.referred_by_category || "—"})` : "—"}</td>
                                        <td className="py-2.5 pr-3 text-xs font-bold text-navy">{a.direct_referrals}</td>
                                        <td className="py-2.5 pr-3 text-xs font-bold text-navy">{a.network_referrals}</td>
                                        <td className="py-2.5 pr-3 text-xs">{a.bookings}</td>
                                        <td className="py-2.5 pr-3 text-xs font-bold text-navy">₹{Number(a.revenue || 0).toLocaleString("en-IN")}</td>
                                        <td className="py-2.5 whitespace-nowrap text-right">
                                            <button onClick={() => openTree(a.id)} title="View hierarchy" data-testid={`acq-tree-${a.id}`} className="text-stone-400 hover:text-navy mr-2"><GitBranch className="h-4 w-4 inline" /></button>
                                            <button onClick={() => doChangeReferrer(a)} title="Change referrer" data-testid={`acq-change-${a.id}`} className="text-stone-400 hover:text-saffron"><KeyRound className="h-4 w-4 inline" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {rows && rows.length === 0 && <p className="py-6 text-sm text-stone-500 text-center">No associates match the filters.</p>}
                </div>
            </div>

            {/* C. Hierarchy drawer */}
            {tree && (
                <div className="fixed inset-0 z-50 flex justify-end bg-navy/40" onClick={() => setTree(null)} data-testid="hierarchy-drawer">
                    <div className="w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between">
                            <h3 className="font-display font-black text-lg text-navy">Referral Hierarchy</h3>
                            <button onClick={() => setTree(null)} className="text-stone-400 hover:text-navy" data-testid="hierarchy-close"><X className="h-5 w-5" /></button>
                        </div>
                        {tree.direct_referrer && (
                            <div className="mt-3 rounded-lg border border-gold/40 bg-gold/5 p-3">
                                <p className="text-[10px] font-bold uppercase text-stone-500">Direct referrer (above)</p>
                                <p className="font-bold text-navy">{tree.direct_referrer.name} <span className="font-mono text-[10px] text-stone-400">{tree.direct_referrer.associate_id}</span></p>
                            </div>
                        )}
                        <div className="mt-3"><HierarchyTree root={tree.root} /></div>
                        {(!tree.root.children || tree.root.children.length === 0) && <p className="mt-2 text-xs text-stone-400">No downline referrals yet.</p>}
                    </div>
                </div>
            )}
        </div>
    );
}
